import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiResponseDTOSelfieStatusResponseDTO,
  SelfieStatusResponseDTO,
  UsersApiService,
} from "@zenith/services/user";

// Enable debugging
const DEBUG = process.env.NODE_ENV === "development";
const log = (...args: any[]) =>
  DEBUG && console.log("[useSelfieVerificationPolling]", ...args);

/**
 * A simpler polling hook for selfie verification that directly uses setInterval
 */
export default function useSelfieVerificationPolling(
  isPollingActive: boolean,
  isProcessing: boolean,
  jobId: string,
  enabled = true
) {
  // States
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [polling, setPolling] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastPollingAttempt, setLastPollingAttempt] = useState<number | null>(
    null
  );
  const [retryCount, setRetryCount] = useState<number>(0);

  // Refs
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const consecutiveErrorsRef = useRef<number>(0);

  // Config values
  const pollInterval = 5000; // 3 seconds
  const maxErrors = 5; // Max consecutive errors before stopping

  // Log initialization parameters
  useEffect(() => {
    log("Initialization params:", {
      isPollingActive,
      isProcessing,
      jobId,
      enabled,
    });
  }, [isPollingActive, isProcessing, jobId, enabled]);

  // Function to stop polling
  const stopPolling = useCallback(() => {
    if (!intervalRef.current) return;

    log("Stopping polling");
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setPolling(false);

    // Add an additional safeguard to prevent any chance of restarting
    if (isMountedRef.current) {
      log("Setting explicit flags to prevent polling restart");
    }
  }, []);

  // Function to poll the selfie status
  const fetchSelfieStatus = useCallback(async () => {
    if (!jobId) {
      log("No job ID provided, cannot poll");
      return;
    }

    setLastPollingAttempt(Date.now());
    log(`Polling selfie status for job ${jobId}...`);

    try {
      // Switch back to using the API service for proper authentication
      const response = await UsersApiService.getSelfieStatus(jobId);

      if (!isMountedRef.current) return;

      // ULTRA SPECIFIC CHECK for the exact error structure in the screenshot
      if (
        response?.success === true &&
        response?.data &&
        (response.data as any).status === "ERROR_IN_VERIFICATION" &&
        (response.data as any).message === "GENERAL_ERROR_IN_VERIFICATION"
      ) {
        console.warn(
          `[useSelfieVerificationPolling] EXACT ERROR MATCH - Screenshot format detected:`,
          response
        );

        // Force status update
        setStatus("ERROR_IN_VERIFICATION");

        // Force immediate stop
        if (intervalRef.current) {
          console.error("⚠️ EMERGENCY STOP: Exact error pattern detected");
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        // Set polling to false
        setPolling(false);

        // Trigger error on the component
        setError(
          new Error("ERROR_IN_VERIFICATION: GENERAL_ERROR_IN_VERIFICATION")
        );

        return; // Exit immediately
      }

      // Direct check for the exact error pattern shown in the screenshot
      if (
        response?.data &&
        ((response.data as any).status === "ERROR_IN_VERIFICATION" ||
          (response.data as any).message === "GENERAL_ERROR_IN_VERIFICATION")
      ) {
        console.warn(
          `[useSelfieVerificationPolling] ERROR detected in response:`,
          response
        );

        // Force status update
        setStatus("ERROR_IN_VERIFICATION");

        // Force immediate stop
        if (intervalRef.current) {
          log("EMERGENCY STOP: Clearing interval due to ERROR_IN_VERIFICATION");
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        // Set polling to false
        setPolling(false);

        return; // Exit immediately
      }

      // Check if response.data exists and has a status
      if (response?.data?.status) {
        const currentStatus = response.data.status;
        log(`Received status: ${currentStatus}`);

        // Additional check for error messages in the response
        const responseMessage = response.data.message;

        // Emergency check for any error indicators
        const hasErrorIndicator =
          currentStatus === "ERROR_IN_VERIFICATION" ||
          (typeof responseMessage === "string" &&
            (responseMessage === "GENERAL_ERROR_IN_VERIFICATION" ||
              responseMessage.includes("ERROR"))) ||
          (typeof currentStatus === "string" &&
            currentStatus.includes("ERROR"));

        if (hasErrorIndicator) {
          console.warn(
            `[useSelfieVerificationPolling] Error indicator found:`,
            {
              status: currentStatus,
              message: responseMessage,
              fullResponse: response,
            }
          );

          // Force status to ERROR for any error indication
          setStatus("ERROR_IN_VERIFICATION");

          // Force immediate stop
          if (intervalRef.current) {
            log("EMERGENCY STOP: Clearing interval due to error indication");
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          // Set polling to false
          setPolling(false);

          return; // Exit immediately
        }

        // Check terminal statuses
        const terminalStatuses = [
          "VERIFIED",
          "VERIFIED_SUCCESSFULLY",
          "NOT_VERIFIED",
          "ERROR_IN_VERIFICATION",
        ];

        if (terminalStatuses.includes(currentStatus)) {
          log(`Terminal status detected: ${currentStatus}, stopping polling`);

          // Update status first
          setStatus(currentStatus);

          // Then immediately stop polling
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          setPolling(false);
          return; // Exit immediately
        }

        // For non-terminal statuses, just update status and continue
        setStatus(currentStatus);
        consecutiveErrorsRef.current = 0;
      } else {
        log("Response received but no status found", response);
        consecutiveErrorsRef.current++;
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      // Log error in detail
      console.error(
        `[useSelfieVerificationPolling] Error polling job ${jobId}:`,
        err
      );

      consecutiveErrorsRef.current++;
      setError(err instanceof Error ? err : new Error(String(err)));

      // Any error should force stop polling after a few retries
      if (consecutiveErrorsRef.current >= 2) {
        console.warn(
          `[useSelfieVerificationPolling] ${consecutiveErrorsRef.current} consecutive errors, emergency stop`
        );

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        setPolling(false);
        setStatus("ERROR_IN_VERIFICATION"); // Force error status on consecutive errors
      }
    }
  }, [jobId]);

  // Function to start polling - only if we're allowed to
  const startPolling = useCallback(() => {
    if (intervalRef.current || !jobId) return;

    // Check if we have a terminalStatus already - don't start polling
    if (
      status &&
      [
        "VERIFIED",
        "VERIFIED_SUCCESSFULLY",
        "NOT_VERIFIED",
        "ERROR_IN_VERIFICATION",
      ].includes(status)
    ) {
      log(
        `Not starting polling because we already have terminal status: ${status}`
      );
      return;
    }

    log(`Starting polling for job ${jobId}`);
    setPolling(true);

    // Do an immediate poll
    fetchSelfieStatus();

    // Then set up the interval
    intervalRef.current = setInterval(fetchSelfieStatus, pollInterval);
  }, [fetchSelfieStatus, jobId, status]);

  // Reset function
  const reset = useCallback(() => {
    log("Resetting polling state");
    stopPolling();
    setStatus(undefined);
    setError(null);
    setLastPollingAttempt(null);
    setRetryCount((prev) => prev + 1);
    consecutiveErrorsRef.current = 0;

    // Restart polling if still active
    if (isPollingActive && enabled && jobId && !isProcessing) {
      startPolling();
    }
  }, [
    stopPolling,
    isPollingActive,
    enabled,
    jobId,
    isProcessing,
    startPolling,
  ]);

  // We need a more robust force stop method
  const forceStopPolling = useCallback(() => {
    log("FORCE STOP called - unconditionally stopping all polling");

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setPolling(false);
  }, []);

  // Update the useEffect that starts/stops polling to also use our forceStop
  useEffect(() => {
    // Don't start polling if we already have an error status
    if (status === "ERROR_IN_VERIFICATION") {
      log("Not starting polling because we have ERROR_IN_VERIFICATION status");
      forceStopPolling();
      return;
    }

    if (isPollingActive && enabled && jobId && !isProcessing) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      forceStopPolling(); // Use the more aggressive stop
    };
  }, [
    isPollingActive,
    enabled,
    jobId,
    isProcessing,
    startPolling,
    stopPolling,
    status,
    forceStopPolling,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Log the current state for debugging
  useEffect(() => {
    log("Current state:", {
      jobId,
      status,
      polling,
      error: error?.message,
      lastPoll: lastPollingAttempt
        ? new Date(lastPollingAttempt).toISOString()
        : null,
      retryCount,
      isPollingActive,
      enabled,
    });
  }, [
    jobId,
    status,
    polling,
    error,
    lastPollingAttempt,
    retryCount,
    isPollingActive,
    enabled,
  ]);

  return {
    status,
    polling,
    error,
    reset,
    lastPollingAttempt,
    retryCount,
  };
}
